import React, { Component } from 'react';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import ExportableTable from 'containers/tables/ExportableTable';
import 'react-table-6/react-table.css';
import ContentTile from 'components/atoms/layout/ContentTile';
import ContentHeader from 'components/atoms/layout/ContentHeader';
import CellDiv from 'components/molecules/tables/components/CellDiv';
import helpers from 'helpers';
import { insertBeforeItem } from 'helpers/utilities';
import RenderingTile from 'components/molecules/RenderingTile';
import Pagination, { RowsPerPageDefault } from 'components/molecules/tables/components/Pagination';
import { generateFooterData, generateHeaders } from 'helpers/tables';

const DEFAULT_SEM_ACCESSORS = [
    'impressions', 'clicks', 'ctr', 'cpc', 'conversions', 'spend'
];
const DEFAULT_GMAIL_ACCESSORS = [
    'impressions', 'gmail_opens', 'gmail_opens_rate', 'gmail_secondary_clicks', 'gmail_secondary_clicks_rate', 'clicks', 'conversions', 'conversion_rate', 'gmail_saves', 'gmail_forwards'
];
const DEFAULT_YOUTUBE_ACCESSORS = [
    'impressions', 'conversions', 'clicks', 'ctr', 'cpc'
];
const DEFAULT_YOUTUBE_VIDEO_VIEWS_ACCESSORS = [
    'impressions', 'videoViews', 'videoViewRate', 'cpvv', 'conversions', 'clicks', 'ctr', 'cpc'
];

class CommonSummaryTable extends Component {
    constructor(props) {
        super(props);

        let columns;
        let data;

        if (props.hasData) {
            const table = CommonSummaryTable.createTableData(props.data, props);
            columns = table.columns;
            data = table.data;
        }

        this.state = {
            data,
            columns,
            hasData: props.hasData,
            exportName: props.exportName,
            params: (this.props.params || {}),
        };
    }

    shouldComponentUpdate(nextProps) {
        return !isEqual(nextProps, this.props);
    }

    static createTableData(data, props) {
        const headers = generateHeaders({
            keys: [
                'impressions', 'totalActivities', 'ecpaActivities', 'startedPlays',
                'completed_plays', 'completion_rate', 'cpcv', 'videoViews', 'videoViewRate',
                'cpvv', 'clicks', 'cpc', 'ctr', 'conversions', 'conversion_revenue', 'cpconv', 'spend',
                'gmail_saves', 'gmail_forwards', 'gmail_secondary_clicks', 'conversion_rate',
                'gmail_secondary_clicks_rate', 'gmail_opens', 'gmail_opens_rate'
            ],
            footerData: generateFooterData({
                data,
                filteredkeys: [
                    'channel', 'campaignName', 'adgroup', 'adgroupName', 'keyword'
                ]
            }),
            colOptions: {
                labelCode: props.entityObjectiveLabelCode
            },
            initial: {
                channel: {
                    Header: 'CHANNEL',
                    accessor: 'channel',
                    type: 'channel',
                    Cell: row => <CellDiv value={helpers.convertToDisplayValue(row.value)} />,
                    Footer: (
                        <CellDiv value='Total' strong />
                    )
                },
                campaignName: {
                    Header: 'CAMPAIGN',
                    accessor: 'campaign_name',
                    minWidth: 250,
                    Cell: row => <CellDiv value={row.value} />,
                    Footer: (
                        <CellDiv value='Total' strong />
                    )
                },
                adgroup: {
                    Header: 'AD GROUP',
                    accessor: 'adgroup',
                    minWidth: 250,
                    Cell: row => <CellDiv value={row.value} />,
                    Footer: (
                        <CellDiv value='Total' strong />
                    )
                },
                adgroupName: {
                    Header: 'AD GROUP',
                    accessor: 'adgroup_name',
                    minWidth: 250,
                    Cell: row => <CellDiv value={row.value} />,
                    Footer: (
                        <CellDiv value='Total' strong />
                    )
                },
                keyword: {
                    Header: 'KEYWORD',
                    accessor: 'keyword',
                    minWidth: 250,
                    Cell: row => <CellDiv value={row.value} />,
                    Footer: (
                        <CellDiv value='Total' strong />
                    )
                },
                conversionName: {
                    Header: props.conversionTermSingular ? `${props.conversionTermSingular.toUpperCase()} NAME` : 'CONVERSION NAME',
                    accessor: 'conversion_name',
                    minWidth: 250,
                    Cell: row => <CellDiv value={row.value} />,
                    Footer: (
                        <CellDiv value='Total' strong />
                    )
                },
                conversionCategory: {
                    Header: 'CATEGORY',
                    accessor: 'conversion_category',
                    Cell: row => <CellDiv value={row.value} />,
                    Footer: (
                        <CellDiv value='' strong />
                    )
                },
            }
        });

        let defaultAccessors;
        switch (props.tableType) {
        case 'sem':
            defaultAccessors = [].concat(props.keyField, DEFAULT_SEM_ACCESSORS);
            break;
        case 'gmail':
            defaultAccessors = [].concat(props.keyField, DEFAULT_GMAIL_ACCESSORS);
            break;
        case 'youtube':
            defaultAccessors = [].concat(props.keyField, DEFAULT_YOUTUBE_ACCESSORS);
            break;
        case 'youtubeVideoViews':
        default:
            defaultAccessors = [].concat(props.keyField, DEFAULT_YOUTUBE_VIDEO_VIEWS_ACCESSORS);
        }

        const { keyField } = props;
        let { accessors = defaultAccessors } = props;
        if (!accessors.includes(keyField)) {
            accessors.unshift(keyField);
        }
        if (props.showTotalActivities && !accessors.includes('totalActivities')) {
            accessors = insertBeforeItem(accessors, 'ecpaActivities', 'totalActivities');
        }

        if (props.showSemCpconv && !accessors.includes('cpconv')) {
            accessors = insertBeforeItem(accessors, 'spend', 'cpconv');
        }

        if (props.showConversionRevenue && !accessors.includes('conversion_revenue')) {
            accessors = insertBeforeItem(accessors, 'spend', 'conversion_revenue');
        }
        const columns = accessors.map(accessor => headers[accessor]);

        return { data, columns };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const nextData = nextProps.data;
        const nextParams = nextProps.params;
        const nextExportName = nextProps.exportName;
        const nextHasData = nextProps.hasData;
        let nextState = null;

        if (nextParams !== prevState.params || nextData !== prevState.data) {
            let columns = [];
            let data = [];

            if (nextHasData) {
                const table = CommonSummaryTable.createTableData(nextData, nextProps);
                columns = table.columns;
                data = table.data;
            }

            nextState = {
                ...prevState,
                columns,
                data,
                exportName: nextExportName,
                hasData: nextHasData,
                params: nextParams
            };
        }
        return nextState;
    }

    render() {
        const { tableType, defaultSorted, keyField, title } = this.props;
        const { data, exportName, columns, hasData } = this.state;

        let sort;
        if (!defaultSorted && tableType) {
            switch (tableType) {
            case 'sem':
                sort = [{
                    id: 'clicks',
                    desc: true
                }];
                break;
            case 'gmail':
                sort = [{
                    id: 'clicks',
                    desc: true
                }];
                break;
            case 'youtube':
                sort = [{
                    id: 'clicks',
                    desc: true
                }];
                break;
            case 'youtubeVideoViews':
            default:
                sort = [{
                    id: 'video_views',
                    desc: true
                }];
            }
        } else {
            sort = defaultSorted || [{
                id: keyField,
                desc: true
            }];
        }

        return hasData ? (
            <ContentTile padding={helpers.contentPadding('table')}>
                <ContentHeader tableData={data} exportName={exportName} type={'table'}>{title}</ContentHeader>
                <ExportableTable
                    key={1}
                    title={this.props.title}
                    exportName={exportName}
                    data={data}
                    columns={columns}
                    getTfootProps={() => ({ style: { backgroundColor: '#E2E2E2', fontWeight: 'bold', boxShadow: 'none' } })}
                    getTheadThProps={() => ({ style: { outline: 0 } })}
                    defaultSorted={sort}
                    showPagination={data.length > RowsPerPageDefault}
                    PaginationComponent={Pagination}
                    defaultPageSize={RowsPerPageDefault}
                    minRows={data.length < RowsPerPageDefault ? data.length : RowsPerPageDefault}
                    className="-highlight"
                />
            </ContentTile>
        ) : (
            <RenderingTile height='400px' show={hasData} />
        );
    }
}

CommonSummaryTable.propTypes = {
    exportName: PropTypes.string.isRequired,
    keyField: PropTypes.string.isRequired,
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    entityObjectiveLabelCode: PropTypes.string,
    conversionTermSingular: PropTypes.string,
    title: PropTypes.string.isRequired,
    accessors: PropTypes.arrayOf(PropTypes.string),
    hasData: PropTypes.bool
};

export default CommonSummaryTable;



// WEBPACK FOOTER //
// ./src/components/molecules/tables/CommonSummaryTable.js